import { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
    const form = useRef();
    const [status, setStatus] = useState('idle'); // 'idle', 'sending', 'success', 'error'
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setStatus('sending');

        // EmailJS Configuration
        const SERVICE_ID = 'service_iqn760a';
        const TEMPLATE_ID = 'template_832lci8';
        const PUBLIC_KEY = 'y-ZNmUUcP_CJoWuQL';

        emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, form.current, PUBLIC_KEY)
            .then((result) => {
                console.log(result.text);
                setStatus('success');
                setFormData({ name: '', email: '', subject: '', message: '' });
                setTimeout(() => setStatus('idle'), 5000);
            }, (error) => {
                console.log(error.text);
                setStatus('error');
                setTimeout(() => setStatus('idle'), 5000);
            });
    };

    return (
        <section id="contact" className="py-20 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-accent font-bold tracking-wide uppercase text-sm mb-3">Get in Touch</h2>
                    <h3 className="text-3xl md:text-5xl font-bold font-heading text-primary mb-6">
                        Let's Discuss Your Project
                    </h3>
                    <p className="text-slate-600 text-lg">
                        Have a query or need a quote? Reach out to us and our team will get back to you shortly.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-16">
                    {/* Contact Info */}
                    <div className="lg:col-span-2 space-y-8">
                        <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-100 h-full">
                            <div className="space-y-6">
                                <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                                    <div className="bg-accent/10 p-3 rounded-lg text-accent">
                                        <MapPin size={24} />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-primary mb-1">Corporate Office</h4>
                                        <p className="text-slate-600 leading-relaxed">
                                            <strong>Uma Ispat Private Limited</strong><br />
                                            Plot No. 5, Co-operative Colony,<br />
                                            Bokaro Steel City,<br />
                                            Jharkhand – 827001
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                                    <div className="bg-accent/10 p-3 rounded-lg text-accent">
                                        <MapPin size={24} />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-primary mb-1">Warehouse & Plant</h4>
                                        <p className="text-slate-600 leading-relaxed">
                                            <strong>Uma Ispat Private Limited</strong><br />
                                            Plot No. III-3/D(P), Industrial Area Road,<br />
                                            Gorabali, Balidih Industrial Area Phase 3,<br />
                                            Bokaro Steel City – 827014
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                                    <div className="bg-accent/10 p-3 rounded-lg text-accent">
                                        <MapPin size={24} />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-primary mb-1">Registered Office</h4>
                                        <p className="text-slate-600 leading-relaxed">
                                            <strong>Uma Niwas</strong><br />
                                            698 GB Block, PNEW ALIPORE<br />
                                            P.S. NEW ALIPORE, KOLKATA<br />
                                            West Bengal, India – 700053
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="bg-accent/10 p-3 rounded-lg text-accent">
                                        <Mail size={24} />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-primary mb-1">Email Us</h4>
                                        <a href="mailto:info@umaispat.com" className="text-slate-600 hover:text-accent transition-colors">
                                            info@umaispat.com
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="bg-accent/10 p-3 rounded-lg text-accent">
                                        <Phone size={24} />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-primary mb-1">Call Us</h4>
                                        <p className="text-slate-600">+91 90519 99959</p>
                                        <p className="text-slate-600">+91 82941 77001</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="lg:col-span-3">
                        <form ref={form} onSubmit={handleSubmit} className="bg-white p-8 sm:p-10 rounded-2xl shadow-lg border border-slate-100 relative overflow-hidden">
                            {/* Success Overlay */}
                            {status === 'success' && (
                                <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center p-6 text-center z-10 animate-fade-in">
                                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                                        <CheckCircle size={32} />
                                    </div>
                                    <h4 className="text-2xl font-bold text-primary mb-2">Message Sent!</h4>
                                    <p className="text-slate-600">
                                        Thank you for reaching out. Our team will get back to you shortly.
                                    </p>
                                    <button
                                        onClick={() => setStatus('idle')}
                                        className="mt-6 text-accent font-semibold hover:underline"
                                    >
                                        Send another message
                                    </button>
                                </div>
                            )}

                            {/* Error Overlay */}
                            {status === 'error' && (
                                <div className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center p-6 text-center z-10 animate-fade-in">
                                    <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4">
                                        <AlertCircle size={32} />
                                    </div>
                                    <h4 className="text-2xl font-bold text-primary mb-2">Something went wrong</h4>
                                    <p className="text-slate-600">
                                        We couldn't send your message. Please try again or contact us via phone.
                                    </p>
                                    <button
                                        onClick={() => setStatus('idle')}
                                        className="mt-6 text-accent font-semibold hover:underline"
                                    >
                                        Try again
                                    </button>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                                        placeholder="John Doe"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all"
                                        placeholder="john@company.com"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="mb-6">
                                <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                                <select
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all bg-white"
                                >
                                    <option value="">Select a topic</option>
                                    <option value="Quote Request">Request a Quote</option>
                                    <option value="General Inquiry">General Inquiry</option>
                                    <option value="Partnership">Partnership</option>
                                </select>
                            </div>

                            <div className="mb-8">
                                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows="4"
                                    value={formData.message}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none transition-all resize-none"
                                    placeholder="How can we help you?"
                                    required
                                ></textarea>
                            </div>

                            <button
                                type="submit"
                                disabled={status === 'sending'}
                                className="w-full bg-primary hover:bg-secondary text-white font-bold py-4 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {status === 'sending' ? (
                                    <>
                                        <Loader2 size={20} className="animate-spin" /> Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send size={20} /> Send Message
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
